import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react'

import withConfig from '../../../wrappers/withConfig'
import { AppStateContext } from '../AppStateContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import toast from '../../../elem/Toast'
import { filterModes } from './FilterModeSelector'
import { generateDatePostParams } from '../../../../utils/chart/values'
import { ParameterContext } from '../../../wrappers/ParameterContext'

const DataContext = createContext(null)

export default withConfig(({ config, children }) => {
    const { API_URL } = config
    const { authenticatedFetch } = useContext(APIRequestContext)
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)
    const { params } = useContext(ParameterContext)
    const [boxData, setBoxData] = useState([])
    const [scatterData, setScatterData] = useState([])
    const [loading, setLoading] = useState(false)

    const [filterMode, setFilterMode] = useState(filterModes[0]) //Selected Wells, All Wells

    const chartRef = useRef(null)

    const includeAllData = useMemo(() => filterMode === 'All Wells', [
        filterMode,
    ])

    const fetchChartData = useCallback(
        (allData, selectedFeatures) => {
            setLoading(true)

            const dates = generateDatePostParams(params)
            const ids = selectedFeatures.map(x => x.get('ClearinghouseNumber'))

            const body = JSON.stringify({
                ClearinghouseNums: ids,
                allData,
                dates,
            })
            authenticatedFetch(`${API_URL}/boxChart`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body,
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    setBoxData(response.boxData)
                    setScatterData(response.scatterData)
                })
                .catch(e => {
                    toast({
                        level: 'error',
                        message:
                            'Box Plot: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [authenticatedFetch, setBoxData, setScatterData, setLoading, params]
    )

    //only call fetchChartData if includeAllData is all Wells
    useEffect(() => {
        // if include all data is true then only
        if (includeAllData) {
            setBoxData([])
            setScatterData([])
            fetchChartData(true, [])
        }
    }, [fetchChartData, includeAllData])

    //call fetchChartData when selected features change
    useEffect(() => {
        if (
            filterMode === 'Selected Wells' &&
            selectedFeatures &&
            selectedFeatures.length
        ) {
            setBoxData([])
            setScatterData([])
            fetchChartData(false, selectedFeatures)
        }
    }, [fetchChartData, selectedFeatures, filterMode])

    return (
        <DataContext.Provider
            value={{
                boxData,
                scatterData,
                loading,
                filterMode,
                setFilterMode,
                chartRef,
            }}
        >
            {children}
        </DataContext.Provider>
    )
})

export { DataContext }
